import { create } from 'zustand'
import { getUser, getUserExtend } from '@/common/utils.ts'
import { covertMenu, resolveCAASMenu } from '@/utils/menu.ts'
import { getTheme, setThemeRoot } from '@/utils/common.ts'
import ShjGreenTheme from '@/themes/shj-green/theme-token.ts'
import LightBlue from '@/themes/light-blue.ts'
import RedTheme from '@/themes/lr-red/theme-token.ts'
import GalaxyPurple from '@/themes/galaxy-purple.ts'
import GeekBlue from '@/themes/geek-blue.ts'
import SunsetYellow from '@/themes/sunset-yellow.ts'
import { batchSave, cacheQuery } from '@/services/cache.ts'
import { triggerEvent } from '@/utils/customEvent.ts'

const localTheme = getTheme()
export const defaultTheme = Object.values(localTheme).length ? localTheme : ShjGreenTheme || {}

export const themeList = [
  { theme: ShjGreenTheme, key: 'green', name: '碧山绿', color: '#14655B' },
  { theme: LightBlue, key: 'lightBlue', name: '拂晓蓝', color: '#0053CF' },
  { theme: RedTheme, key: 'red', name: '火山红', color: '#D9001B' },
  { theme: GalaxyPurple, key: 'purple', name: '星空紫', color: '#14655B' },
  { theme: GeekBlue, key: 'geekBlue', name: '极客蓝', color: '#0053CF' },
  { theme: SunsetYellow, key: 'yellow', name: '日落黄', color: '#D9001B' },
]

const defaultState = {
  theme: defaultTheme,
  menuSource: [],
  menu: [],
  menuActive: null,
  menuLoading: false,
  menuCollapsed: localStorage.getItem('menuCollapsed') || false,
  menuCollapseAnimating: false,
  menuLevel: 2,
  user: getUser() || {},
  userExtend: getUserExtend() || {},
  selectedApp: null, // 侧边栏 选中的应用
  globalTitle: '工作台',
  globalAsideCollapsed: false,
  hasAuthMenu: false,
  selectedMenu: null, // 选中的第二级菜单
  selectedSubMenu: null, // 选中的第三级菜单
  menuRouteCache: {}, // 菜单路由缓存
  routeHistory: sessionStorage.getItem('ROUTE_HISTORY')?.split?.(',') || [],
  identities: [],
  useInfo: {},
  tipsVisible: false,
  activeTabKey: 'ALL',
}

const useGlobalStore = create((set, get: any) => ({
  ...defaultState,
  setActiveTabKey: activeTabKey => set({ activeTabKey }),
  setTheme: theme => set({ theme }),
  setMenuLoading: menuLoading => set({ menuLoading }),
  setMenu: menu =>
    set(() => {
      const [menus] = covertMenu(resolveCAASMenu(menu))
      return { menu: menus }
    }),
  setMenuSource: menuSource => set({ menuSource }),
  setMenuActive: menuActive => set(() => ({ menuActive })),
  setMenuRouteCache: (key, path) =>
    set(state => ({ menuRouteCache: { ...state.menuRouteCache, [key]: path } })),
  setMenuCollapsed: menuCollapsed => {
    if (menuCollapsed) {
      localStorage.setItem('menuCollapsed', menuCollapsed)
    } else {
      localStorage.removeItem('menuCollapsed')
    }
    set(() => ({ menuCollapsed }))
  },
  setUserInfo: info => set(() => ({ useInfo: info })),
  setTipsVisible: visible => set(() => ({ tipsVisible: visible })),
  setMenuCollapsedStart: () => set(() => ({ menuCollapseAnimating: true })),
  setMenuCollapsedEnd: () => set(() => ({ menuCollapseAnimating: false })),
  setMenuLevel: menuLevel => set(() => ({ menuLevel })),
  setUser: user => set(() => ({ user })),
  setUserExtend: userExtend => set(() => ({ userExtend })),
  updateSelectedApp: selectedApp => set(() => ({ selectedApp })),
  updateSelectedMenu: selectedMenu => set(() => ({ selectedMenu })),
  updateSelectedSubMenu: selectedSubMenu => set(() => ({ selectedSubMenu })),
  updateGlobalTitle: globalTitle => set(() => ({ globalTitle })),
  updateGlobalAsideCollapsed: globalAsideCollapsed => set(() => ({ globalAsideCollapsed })),
  setHasAuthMenu: hasAuthMenu => set(() => ({ hasAuthMenu })),
  saveRouteHistory: path =>
    set(state => {
      const routeHistory = [path, ...state.routeHistory.filter(d => d !== path)].slice(0, 100)
      sessionStorage.setItem('ROUTE_HISTORY', routeHistory.join(','))
      return { routeHistory }
    }),
  saveTheme: async (themeKey, theme) => {
    localStorage.setItem('ANTD_CONFIG_PROVIDER', JSON.stringify(theme))
    const user = getUser()
    const key = `USER_THEME_${user.id}`
    return batchSave([{ key, value: themeKey }])
  },
  fetchTheme: async (userData?) => {
    const { setTheme } = get()
    const user = userData || getUser()
    const key = `USER_THEME_${user.id || user.userId}`
    return cacheQuery([key]).then((res: any) => {
      const themeKey = res[key]?.value
      const theme = themeList.find(item => item.key === themeKey)?.theme
      if (theme) {
        setTheme(theme)
        triggerEvent('theme-change', theme)
        setThemeRoot(theme)
        localStorage.setItem('ANTD_CONFIG_PROVIDER', JSON.stringify(theme))
      }
    })
  },
  setIdentities: identities => set(() => ({ identities })),

  getCurrentIdentity: identities => {
    const user = getUser()
    if (!user?.currentRoleIds) return null
    return identities.find(d => user.currentRoleIds.includes(d.roleId))
  },
}))

export default useGlobalStore
